import {
  getLeoUuid,
  LeoUUID,
  getString,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
/* eslint-disable import/export */
export abstract class ValidateAndUploadSurveyRulesRPC implements
  LeoRPC<ValidateAndUploadSurveyRulesRPC.Request, ValidateAndUploadSurveyRulesRPC.Response, ValidateAndUploadSurveyRulesRPC.Errors.Errors>
{
  abstract execute(
    request: ValidateAndUploadSurveyRulesRPC.Request
  ): Promise<LeoRPCResult<ValidateAndUploadSurveyRulesRPC.Response, ValidateAndUploadSurveyRulesRPC.Errors.Errors>>;
}

export namespace ValidateAndUploadSurveyRulesRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  export interface RequestDTO {
    surveyId: string;
    projectId: string;
    documentId: string;
  }
  
  export class Request implements LeoRPCRequest {
    readonly isValidateAndUploadSurveyRulesRPCRequest: boolean = true;
  
    readonly surveyId: LeoUUID;
    readonly projectId: LeoUUID;
    readonly documentId: LeoUUID;
  
  
    constructor(
    surveyId: LeoUUID, 
    projectId: LeoUUID, 
    documentId: LeoUUID
    ) {
      this.surveyId = surveyId;
      this.projectId = projectId;
      this.documentId = documentId;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const surveyId = getLeoUuid(dto, "surveyId", InvalidRequestSurveyIdError);
      const projectId = getLeoUuid(dto, "projectId", InvalidRequestProjectIdError);
      const documentId = getLeoUuid(dto, "documentId", InvalidRequestDocumentIdError);
      return new Request(
        surveyId,
        projectId,
        documentId
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          surveyId: this.surveyId.toDTO(),
          projectId: this.projectId.toDTO(),
          documentId: this.documentId.toDTO()
      };
      return dto;
    }
    }
  
  Request.prototype.toString = function toString(): string {
    return `ValidateAndUploadSurveyRulesRPC(surveyId='${this.surveyId}',projectId='${this.projectId}',documentId='${this.documentId}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  export interface ResponseDTO {}

  export class Response implements LeoRPCResponse {
    static fromDTO(): Response {
      return new Response();
    }

    toDTO(): ResponseDTO {
      return {}
    }
  }

  Response.prototype.toString = function toString(): string {
    return `ValidateAndUploadSurveyRulesRPC.Response()`;
  };

  export enum RPCError {
    InvalidFile = "INVALID_FILE",
    UnknownFile = "UNKNOWN_FILE",
    InvalidSurveyId = "INVALID_SURVEY_ID",
    InvalidProjectId = "INVALID_PROJECT_ID",
    ProjectAlreadyArchived = "PROJECT_ALREADY_ARCHIVED"
  }

  export namespace Errors {
    
    export interface InvalidFileDTO {
      code: string;
      invalidFileDetails: string;
    }
    
    export class InvalidFile extends LeoRPCError<RPCError> {
      readonly isInvalidFile: boolean = true;
    
      readonly invalidFileDetails: string;
    
      constructor(
      invalidFileDetails: string
      ) {
        const code = RPCError.InvalidFile;
        super(code);
        InvalidFile.validateInvalidFileDetails(invalidFileDetails);
        this.invalidFileDetails = invalidFileDetails;
      }
    
      static fromDTO(dto: InvalidFileDTO): InvalidFile {
        const invalidFileDetails = getString(dto, "invalidFileDetails", InvalidResponseInvalidFileInvalidFileDetailsError);
        return new InvalidFile(
          invalidFileDetails
        );
      }
    
      toDTO(): InvalidFileDTO {
        const dto: InvalidFileDTO = {
          code: this.code,
          invalidFileDetails: this.invalidFileDetails
        };
        return dto;
      }
    
      private static validateInvalidFileDetails = (invalidFileDetails: string): void => {
        if(invalidFileDetails.trim().length === 0) {
          throw new InvalidResponseInvalidFileInvalidFileDetailsNonEmptyError(
            `Attribute invalidFileDetails cannot be an empty string.`
          );
        }
      }
      
    }
    
    InvalidFile.prototype.toString = function toString(): string {
      return `ValidateAndUploadSurveyRulesRPC.INVALID_FILE(invalidFileDetails='${this.invalidFileDetails}')`;
    };
    
    
    export interface UnknownFileDTO {
      code: string;
    }
    
    export class UnknownFile extends LeoRPCError<RPCError> {
      readonly isUnknownFile: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.UnknownFile;
        super(code);
      }
    
    
      toDTO(): UnknownFileDTO {
        const dto: UnknownFileDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    UnknownFile.prototype.toString = function toString(): string {
      return `ValidateAndUploadSurveyRulesRPC.UNKNOWN_FILE()`;
    };
    
    
    export interface InvalidSurveyIdDTO {
      code: string;
    }
    
    export class InvalidSurveyId extends LeoRPCError<RPCError> {
      readonly isInvalidSurveyId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidSurveyId;
        super(code);
      }
    
    
      toDTO(): InvalidSurveyIdDTO {
        const dto: InvalidSurveyIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidSurveyId.prototype.toString = function toString(): string {
      return `ValidateAndUploadSurveyRulesRPC.INVALID_SURVEY_ID()`;
    };
    
    
    export interface InvalidProjectIdDTO {
      code: string;
    }
    
    export class InvalidProjectId extends LeoRPCError<RPCError> {
      readonly isInvalidProjectId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidProjectId;
        super(code);
      }
    
    
      toDTO(): InvalidProjectIdDTO {
        const dto: InvalidProjectIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidProjectId.prototype.toString = function toString(): string {
      return `ValidateAndUploadSurveyRulesRPC.INVALID_PROJECT_ID()`;
    };
    
    
    export interface ProjectAlreadyArchivedDTO {
      code: string;
    }
    
    export class ProjectAlreadyArchived extends LeoRPCError<RPCError> {
      readonly isProjectAlreadyArchived: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.ProjectAlreadyArchived;
        super(code);
      }
    
    
      toDTO(): ProjectAlreadyArchivedDTO {
        const dto: ProjectAlreadyArchivedDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    ProjectAlreadyArchived.prototype.toString = function toString(): string {
      return `ValidateAndUploadSurveyRulesRPC.PROJECT_ALREADY_ARCHIVED()`;
    };
    

    export type Errors = InvalidFile | UnknownFile | InvalidSurveyId | InvalidProjectId | ProjectAlreadyArchived;
    export type ErrorsDTO = InvalidFileDTO | UnknownFileDTO | InvalidSurveyIdDTO | InvalidProjectIdDTO | ProjectAlreadyArchivedDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.InvalidFile:
          return InvalidFile.fromDTO(dto as InvalidFileDTO);
        case RPCError.UnknownFile:
          return new UnknownFile();
        case RPCError.InvalidSurveyId:
          return new InvalidSurveyId();
        case RPCError.InvalidProjectId:
          return new InvalidProjectId();
        case RPCError.ProjectAlreadyArchived:
          return new ProjectAlreadyArchived();
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestSurveyIdError extends ValidateAndUploadSurveyRulesRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSurveyIdError";
  }
}

export class InvalidRequestProjectIdError extends ValidateAndUploadSurveyRulesRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestProjectIdError";
  }
}

export class InvalidRequestDocumentIdError extends ValidateAndUploadSurveyRulesRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestDocumentIdError";
  }
}


export class InvalidResponseInvalidFileError extends ValidateAndUploadSurveyRulesRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidFileError";
  }
}

export class InvalidResponseInvalidFileInvalidFileDetailsError extends InvalidResponseInvalidFileError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidFileInvalidFileDetailsError";
  }
}

export class InvalidResponseInvalidFileInvalidFileDetailsNonEmptyError extends InvalidResponseInvalidFileInvalidFileDetailsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidFileInvalidFileDetailsNonEmptyError";
  }
}
export class InvalidResponseUnknownFileError extends ValidateAndUploadSurveyRulesRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseUnknownFileError";
  }
}

export class InvalidResponseInvalidSurveyIdError extends ValidateAndUploadSurveyRulesRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidSurveyIdError";
  }
}

export class InvalidResponseInvalidProjectIdError extends ValidateAndUploadSurveyRulesRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidProjectIdError";
  }
}

export class InvalidResponseProjectAlreadyArchivedError extends ValidateAndUploadSurveyRulesRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseProjectAlreadyArchivedError";
  }
}

